import {
    RealtimeQueryData,
    ParsedObject,
    ParsedObjectStateData,
    RawConstraints,
    Constraints,
    ZonebjectBase,
    ZoneObject,
    Sensor,
    DataMeasurement,
    DataType,
    INDICATOR_DATA_TYPES,
    AverageValues,
    Mode,
} from '../types'
import mean from 'lodash/mean'

export const parseZoneObject = (zoneObject: ZoneObject, sitePilot = 1): Omit<ParsedObject, 'stateData'> => {
    return {
        ...zoneObject,
        pilot: Boolean(zoneObject.pilot * sitePilot),
    }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const arrayToMap = <T extends Record<string, any>>(array: T[], key: keyof T): Record<T[typeof key], T> => {
    return array.reduce(
        (acc, item) => {
            acc[item[key]] = item
            return acc
        },
        {} as Record<T[typeof key], T>
    )
}

export const parseSensorData = (
    object: Pick<ZonebjectBase, 'objectId' | 'pmsId'>,
    allSensors: Sensor[],
    allDataMeasurements: Record<number, DataMeasurement>
): ParsedObjectStateData => {
    const { objectId, pmsId } = object
    const sensors = allSensors.filter((sensor) => sensor.objectId === objectId || (pmsId && sensor.pmsId === pmsId))
    return sensors.reduce((acc, sensor) => {
        const dataMeasurement = allDataMeasurements[sensor.sensorId]
        if (!dataMeasurement) {
            return acc
        }
        return {
            ...acc,
            [sensor.dataTypeId]:
                sensor.dataTypeId !== DataType.ERROR_MESSAGE ||
                dataMeasurement?.raw == 'ok' ||
                dataMeasurement?.raw == 'OK' ||
                dataMeasurement?.raw == 'OFF'
                    ? dataMeasurement.value
                    : dataMeasurement?.raw,
        }
    }, {} as ParsedObjectStateData)
}

const parseRawConstraints = (rawContraints: RawConstraints | undefined, mode: Mode): Constraints | undefined => {
    if (!rawContraints) {
        return undefined
    }
    if (mode === Mode.HEAT) {
        return {
            min: rawContraints.constraintMinHeat,
            max: rawContraints.constraintMaxHeat,
        }
    }
    if (mode === Mode.COOL) {
        return {
            min: rawContraints.constraintMin,
            max: rawContraints.constraintMax,
        }
    }
    if (mode === Mode.AUTO) {
        return {
            min: Math.min(rawContraints.constraintMin, rawContraints.constraintMinHeat),
            max: Math.max(rawContraints.constraintMax, rawContraints.constraintMaxHeat),
        }
    }
    return undefined
}
type SnapshotData = Record<string, unknown>

const SNAPSHOT_INDICATOR_KEYS: Partial<Record<DataType, string>> = {
    [DataType.STATE]: 'Drive-sensor',
    [DataType.TEMPERATURE]: 'InletTemp-sensor',
    [DataType.SETPOINT_TEMPERATURE]: 'SetTemp-sensor',
    [DataType.MODE]: 'Mode-sensor',
    [DataType.OCCUPANCY]: 'Occupancy-sensor',
}

const parseSnapshot = (snapshot: unknown): SnapshotData => {
    if (!snapshot) {
        return {}
    }
    if (typeof snapshot === 'object') {
        return snapshot as SnapshotData
    }
    if (typeof snapshot !== 'string') {
        return {}
    }
    try {
        return JSON.parse(snapshot) as SnapshotData
    } catch (error) {
        return {}
    }
}

const toNumericSnapshotValue = (value: unknown): number | undefined => {
    if (value === null || value === undefined) {
        return undefined
    }
    if (typeof value === 'number') {
        return Number.isNaN(value) ? undefined : value
    }
    if (typeof value === 'string' && value.trim() !== '') {
        const normalized = Number(value.replace(',', '.'))
        return Number.isNaN(normalized) ? undefined : normalized
    }
    return undefined
}

// Devices report a sentinel (INT32_MIN / 1000 = -2147483.648) when a temperature
// reading is unavailable. Reject any physically implausible value so it never
// enters the model and poisons averages or the zone card.
const TEMPERATURE_DATA_TYPES: DataType[] = [DataType.TEMPERATURE, DataType.SETPOINT_TEMPERATURE]
const isPlausibleTemperature = (value: number): boolean => Math.abs(value) < 100

const enrichStateDataWithSnapshot = (
    stateData: ParsedObjectStateData,
    snapshot: SnapshotData
): ParsedObjectStateData => {
    if (!snapshot || Object.keys(snapshot).length === 0) {
        return stateData
    }
    return (Object.entries(SNAPSHOT_INDICATOR_KEYS) as Array<[string, string]>).reduce(
        (acc, [dataTypeKey, snapshotKey]) => {
            const dataTypeId = Number(dataTypeKey) as DataType
            const numericValue = toNumericSnapshotValue(snapshot[snapshotKey])
            if (numericValue === undefined) {
                return acc
            }
            if (TEMPERATURE_DATA_TYPES.includes(dataTypeId) && !isPlausibleTemperature(numericValue)) {
                return acc
            }
            acc[dataTypeId] = dataTypeId === DataType.STATE ? (numericValue > 0 ? 1 : 0) : numericValue
            return acc
        },
        { ...stateData }
    )
}

const getSnapshotNumericValue = (snapshot: SnapshotData, key: string): number | undefined => {
    return toNumericSnapshotValue(snapshot[key])
}

export const parseRealtimeObjects = (
    realtimeData: RealtimeQueryData,
    siteConstraints: Record<number, RawConstraints> = {},
    sitePilot: number = 1
): ParsedObject[] => {
    const dataMeasurementsMap = arrayToMap(realtimeData.dataMeasurements, 'sensorId')
    return realtimeData.objects
        .filter((zoneObject) => !(zoneObject.isSlave || zoneObject.hidden))
        .map((zoneObject) => {
            const snapshot = parseSnapshot(zoneObject?.snapshot)
            const sensorStateData = parseSensorData(zoneObject, realtimeData.sensors, dataMeasurementsMap)
            const stateData = enrichStateDataWithSnapshot(sensorStateData, snapshot)
            // Read the raw value, not its numeric coercion: the Gridone plugin reports
            // 'idle' as a string next to the numeric modes, and coercing it first
            // dropped it silently.
            const modeSnapshotValue = snapshot['Mode-sensor']
            const mode = parseMode(modeSnapshotValue ?? stateData[DataType.MODE])
            const occupancyValue = getSnapshotNumericValue(snapshot, 'Pms-sensor') ?? stateData[DataType.OCCUPANCY]
            const snapshotStateValue = getSnapshotNumericValue(snapshot, 'Drive-sensor')
            const normalizedSnapshotState =
                snapshotStateValue === undefined ? undefined : snapshotStateValue > 0 ? 1 : 0
            const stateValue = normalizedSnapshotState ?? stateData[DataType.STATE]
            return {
                ...parseZoneObject(zoneObject, sitePilot),
                stateData,
                mode,
                state: Boolean(stateValue),
                temperature: stateData[DataType.TEMPERATURE],
                setpointTemperature: stateData[DataType.SETPOINT_TEMPERATURE],
                isError: Boolean(stateData[DataType.ERROR_MESSAGE]),
                constraints: parseRawConstraints(siteConstraints[zoneObject.objectId], mode),
                occupied: typeof occupancyValue === 'number' ? Boolean(occupancyValue) : undefined,
                error: stateData[DataType.ERROR_MESSAGE] ? `${stateData[DataType.ERROR_MESSAGE]}` : undefined,
            }
        })
}

// Modes reported as a name rather than a code. The Gridone plugin mixes both:
// numeric codes while conditioning, 'idle' while the unit is on but resting.
const MODE_BY_NAME: Record<string, Mode> = {
    idle: Mode.IDLE,
}

export const parseMode = (mode: unknown): Mode => {
    if (typeof mode === 'string') {
        return MODE_BY_NAME[mode.trim().toLowerCase()] ?? parseMode(toNumericSnapshotValue(mode))
    }
    switch (mode) {
        case 1:
        case 3:
        case 11:
            return Mode.COOL
        case 2:
        case 12:
            return Mode.HEAT

        case 4:
            return Mode.FAN
        case 5:
            return Mode.AUTO
        default:
            return Mode.UNKNOWN
    }
}

export const getStateValuesAverages = (parsedObjects: ParsedObject[]): AverageValues =>
    INDICATOR_DATA_TYPES.map((dataType) => {
        const values = parsedObjects
            .map((object) => object.stateData[dataType])
            .filter((v) => {
                if (typeof v !== 'number' || isNaN(v)) {
                    return false
                }
                if (dataType === DataType.STATE) {
                    return [0, 1].includes(v)
                }
                if (TEMPERATURE_DATA_TYPES.includes(dataType)) {
                    return isPlausibleTemperature(v)
                }
                return true
            })

        return { type: dataType, average: mean(values) }
    }, {} as AverageValues)
